import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { AuthServiceUAU } from '../../services/authServiceUAU';
import { useTranslation } from 'react-i18next';

interface ModalCambiarPasswordProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ModalCambiarPassword: React.FC<ModalCambiarPasswordProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [passwordActual, setPasswordActual] = useState('');
  const [passwordNuevo, setPasswordNuevo] = useState('');
  const [passwordConfirmar, setPasswordConfirmar] = useState('');
  const { t } = useTranslation();
  const [cargando, setCargando] = useState(false);
  const [mostrarPasswords, setMostrarPasswords] = useState(false);
  const [errores, setErrores] = useState<Record<string, string>>({});

  const validarFormulario = (): boolean => {
    const nuevosErrores: Record<string, string> = {};

    if (!passwordActual) {
      nuevosErrores.passwordActual = 'La contrasena actual es requerida';
    }

    if (!passwordNuevo) {
      nuevosErrores.passwordNuevo = 'La nueva contrasena es requerida';
    } else if (passwordNuevo.length < 8) {
      nuevosErrores.passwordNuevo = 'La contrasena debe tener al menos 8 caracteres';
    }

    if (!passwordConfirmar) {
      nuevosErrores.passwordConfirmar = 'Confirma la nueva contrasena';
    } else if (passwordNuevo !== passwordConfirmar) {
      nuevosErrores.passwordConfirmar = 'Las contrasenas no coinciden';
    }

    if (passwordActual && passwordNuevo && passwordActual === passwordNuevo) {
      nuevosErrores.passwordNuevo = 'La nueva contrasena debe ser diferente a la actual';
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    setCargando(true);

    try {
      const result = await AuthServiceUAU.changePassword({
        password_actual: passwordActual,
        password_nuevo: passwordNuevo,
        password_confirmar: passwordConfirmar
      });

      if (result.success) {
        toast.success('Contrasena actualizada exitosamente');
        limpiarFormulario();
        onClose();

        if (result.requires_relogin) {
          toast('Por seguridad, vuelve a iniciar sesion', { icon: 'info' });
          setTimeout(() => {
            AuthServiceUAU.logout();
            window.location.href = '/login';
          }, 2000);
        } else if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error: any) {
      const mensaje = error.message || 'Error al cambiar la contrasena';
      toast.error(mensaje);

      if (mensaje.toLowerCase().includes('actual') || mensaje.toLowerCase().includes('incorrecta')) {
        setErrores({ passwordActual: 'Contrasena actual incorrecta' });
      }
    } finally {
      setCargando(false);
    }
  };

  const limpiarFormulario = () => {
    setPasswordActual('');
    setPasswordNuevo('');
    setPasswordConfirmar('');
    setErrores({});
  };

  const handleClose = () => {
    limpiarFormulario();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('auth.change_password')}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Password actual */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.current_password')}
            </label>
            <div className="relative">
              <input
                type={mostrarPasswords ? 'text' : 'password'}
                value={passwordActual}
                onChange={(e) => {
                  setPasswordActual(e.target.value);
                  if (errores.passwordActual) {
                    setErrores({ ...errores, passwordActual: '' });
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 ${
                  errores.passwordActual ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ingresa tu contrasena actual"
                disabled={cargando}
              />
            </div>
            {errores.passwordActual && (
              <p className="mt-1 text-sm text-red-600">{errores.passwordActual}</p>
            )}
          </div>

          {/* Password nuevo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.new_password')}
            </label>
            <div className="relative">
              <input
                type={mostrarPasswords ? 'text' : 'password'}
                value={passwordNuevo}
                onChange={(e) => {
                  setPasswordNuevo(e.target.value);
                  if (errores.passwordNuevo) {
                    setErrores({ ...errores, passwordNuevo: '' });
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 ${
                  errores.passwordNuevo ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Minimo 8 caracteres"
                disabled={cargando}
              />
            </div>
            {errores.passwordNuevo && (
              <p className="mt-1 text-sm text-red-600">{errores.passwordNuevo}</p>
            )}
          </div>

          {/* Confirmar password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.confirm_password')}
            </label>
            <div className="relative">
              <input
                type={mostrarPasswords ? 'text' : 'password'}
                value={passwordConfirmar}
                onChange={(e) => {
                  setPasswordConfirmar(e.target.value);
                  if (errores.passwordConfirmar) {
                    setErrores({ ...errores, passwordConfirmar: '' });
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 ${
                  errores.passwordConfirmar ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Repite la nueva contrasena"
                disabled={cargando}
              />
            </div>
            {errores.passwordConfirmar && (
              <p className="mt-1 text-sm text-red-600">{errores.passwordConfirmar}</p>
            )}
          </div>

          {/* Toggle mostrar passwords */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="mostrarPasswords"
              checked={mostrarPasswords}
              onChange={(e) => setMostrarPasswords(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="mostrarPasswords" className="ml-2 text-sm text-gray-600">
              Mostrar contrasenas
            </label>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={cargando}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={cargando}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {cargando ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('common.saving')}
                </>
              ) : (
                t('auth.change_password')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalCambiarPassword;
